import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { useSubjectAccessExport } from '../privacy/queries';
import { useSession } from '../auth/queries';
import type { Contact, Site } from './api';
import {
  useAnonymizeContact,
  useCreateContact,
  useDeleteContact,
  useUpdateContact,
} from './queries';
import { Select } from '../../components/Select';
import { CrmSuggestInput } from './CrmSuggestInput';

interface ContactPanelProps {
  customerId: string;
  contacts: Contact[];
  sites: Site[];
  canWrite: boolean;
}

/**
 * Die Ansprechpartner eines Kunden -- F2-03, F2-10.
 *
 * **Die Pseudonymisierung braucht eine Rueckfrage.** Sie ist unumkehrbar und
 * steht einen Klick neben dem gewoehnlichen Loeschen. Eine Bestaetigung ist
 * hier keine Hoeflichkeit gegenueber dem Anwender, sondern die letzte Stelle,
 * an der sich ein Versehen noch aufhalten laesst.
 *
 * Sie erscheint ausserdem nur mit dem eigenen Recht ``crm.anonymize``. Das
 * Ausblenden ist Bequemlichkeit -- der Server prueft ohnehin --, aber es hilft:
 * Was man nicht sieht, klickt man nicht versehentlich an.
 */
export function ContactPanel({ customerId, contacts, sites, canWrite }: ContactPanelProps) {
  const { t } = useTranslation();
  const session = useSession();
  const create = useCreateContact(customerId);
  const remove = useDeleteContact();
  const anonymize = useAnonymizeContact();
  const update = useUpdateContact();
  const subjectAccess = useSubjectAccessExport();

  // Welcher Kontakt gerade bearbeitet wird -- ``null`` heisst: ein neuer.
  //
  // **Bearbeiten statt loeschen und neu anlegen.** An einem Kontakt haengen
  // Tickets, Aktivitaeten und die Kommunikationshistorie; ihn wegen einer
  // neuen Telefonnummer wegzuwerfen naehme dem Kunden seine Vorgeschichte.
  const [editing, setEditing] = useState<string | null>(null);

  const [values, setValues] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    site_id: '',
    is_primary: false,
    is_authorized_requester: false,
    objects_to_contact: false,
  });
  const [confirming, setConfirming] = useState<string | null>(null);

  const canAnonymize = (session.data?.permissions ?? []).includes('crm.anonymize');
  // Die Auskunft nach Art. 15 haengt an 'privacy.manage' und nicht an
  // 'crm.read': Sie zieht alles zusammen, was ueber die Person gespeichert
  // ist -- Tickets, Beitraege, Aktivitaeten, Umfragen -- und legt es in
  // eine Datei. Der Aufruf wird protokolliert.
  const canExportSubjectAccess = (session.data?.permissions ?? []).includes('privacy.manage');

  const set = <K extends keyof typeof values>(field: K, value: (typeof values)[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const reset = () => {
    setEditing(null);
    setValues({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      position: '',
      site_id: '',
      is_primary: false,
      is_authorized_requester: false,
      objects_to_contact: false,
    });
  };

  return (
    <div className="stack">
      <section className="card card--flush">
        {contacts.length === 0 ? (
          <EmptyState icon="users" title={t('crm.noContacts')} hint={t('crm.noContactsHint')} />
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('crm.contactName')}</th>
                  <th scope="col">{t('auth.email')}</th>
                  <th scope="col">{t('crm.phone')}</th>
                  <th scope="col">{t('crm.role')}</th>
                  {canWrite ? <th scope="col">{t('tickets.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.id}>
                    <td>
                      {contact.first_name} {contact.last_name}
                      {contact.is_primary ? (
                        <>
                          {' '}
                          <Badge tone="info">{t('crm.primaryContact')}</Badge>
                        </>
                      ) : null}
                      {contact.anonymized_at ? (
                        <>
                          {' '}
                          <Badge tone="neutral">{t('crm.anonymized')}</Badge>
                        </>
                      ) : null}
                      {/* Aus der Maildomaene entstanden und von niemandem
                          geprueft. Das Schild bleibt stehen, bis jemand den
                          Eintrag bearbeitet -- es sagt, warum hier ein Name
                          steht, den niemand eingetippt hat. */}
                      {contact.is_auto_created && !contact.anonymized_at ? (
                        <>
                          {' '}
                          <Badge tone="neutral">{t('crm.autoCreated')}</Badge>
                        </>
                      ) : null}
                    </td>
                    <td className="table__muted">{contact.email ?? '—'}</td>
                    <td className="table__muted">{contact.phone ?? '—'}</td>
                    <td className="table__muted">
                      {contact.position ?? '—'}
                      {contact.is_authorized_requester ? (
                        <>
                          {' '}
                          <Badge tone="success">{t('crm.authorizedRequester')}</Badge>
                        </>
                      ) : null}
                    </td>
                    {canWrite ? (
                      <td>
                        {contact.anonymized_at === null ? (
                          <>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                remove.mutate(contact.id);
                              }}
                            >
                              {t('masterdata.delete')}
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                setEditing(contact.id);
                                setValues({
                                  first_name: contact.first_name,
                                  last_name: contact.last_name,
                                  email: contact.email ?? '',
                                  phone: contact.phone ?? '',
                                  position: contact.position ?? '',
                                  site_id: contact.site_id ?? '',
                                  is_primary: contact.is_primary,
                                  is_authorized_requester: contact.is_authorized_requester,
                                  objects_to_contact: contact.objects_to_contact,
                                });
                              }}
                            >
                              {t('masterdata.edit')}
                            </Button>
                            {canAnonymize ? (
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  setConfirming(contact.id);
                                }}
                              >
                                {t('crm.anonymize')}
                              </Button>
                            ) : null}
                            {canExportSubjectAccess ? (
                              <Button
                                variant="ghost"
                                busy={
                                  subjectAccess.isPending &&
                                  subjectAccess.variables.contactId === contact.id
                                }
                                onClick={() => {
                                  subjectAccess.mutate({
                                    contactId: contact.id,
                                    filename: `auskunft-${contact.last_name}.pdf`,
                                  });
                                }}
                              >
                                {t('privacy.subjectAccess')}
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {confirming ? (
        <div className="notice notice--warning" role="alertdialog" aria-live="assertive">
          <p>{t('crm.anonymizeConfirm')}</p>
          <div className="row">
            <Button
              variant="primary"
              busy={anonymize.isPending}
              onClick={() => {
                anonymize.mutate(confirming, {
                  onSuccess: () => {
                    setConfirming(null);
                  },
                });
              }}
            >
              {t('crm.anonymizeConfirmAction')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setConfirming(null);
              }}
            >
              {t('app.cancel')}
            </Button>
          </div>
        </div>
      ) : null}

      {anonymize.isError ? <ErrorNotice error={anonymize.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}
      {subjectAccess.isError ? <ErrorNotice error={subjectAccess.error} /> : null}

      {canWrite ? (
        <section className="card">
          <h2>{editing === null ? t('crm.newContact') : t('crm.editContact')}</h2>

          <div className="field">
            <label className="field__label" htmlFor="contact-last-name">
              {t('crm.lastName')}
            </label>
            <input
              id="contact-last-name"
              className="field__control"
              value={values.last_name}
              onChange={(event) => {
                set('last_name', event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="contact-first-name">
              {t('crm.firstName')}
            </label>
            <input
              id="contact-first-name"
              className="field__control"
              value={values.first_name}
              onChange={(event) => {
                set('first_name', event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="contact-email">
              {t('auth.email')}
            </label>
            <input
              id="contact-email"
              type="email"
              className="field__control"
              value={values.email}
              onChange={(event) => {
                set('email', event.target.value);
              }}
            />
            <span className="field__hint">{t('crm.emailHint')}</span>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="contact-phone">
              {t('crm.phone')}
            </label>
            <input
              id="contact-phone"
              className="field__control"
              value={values.phone}
              onChange={(event) => {
                set('phone', event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="contact-position">
              {t('crm.role')}
            </label>
            <CrmSuggestInput
              id="contact-position"
              value={values.position}
              onChange={(next) => {
                set('position', next);
              }}
              field="contact.position"
            />
          </div>

          {sites.length > 0 ? (
            <div className="field">
              <label className="field__label" htmlFor="contact-site">
                {t('crm.site')}
              </label>
              <Select
                id="contact-site"
                className="field__control"
                value={values.site_id}
                onChange={(event) => {
                  set('site_id', event.target.value);
                }}
              >
                <option value="">{t('crm.noSite')}</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}

          <label className="field__checkbox">
            <input
              type="checkbox"
              checked={values.is_primary}
              onChange={(event) => {
                set('is_primary', event.target.checked);
              }}
            />
            {t('crm.primaryContact')}
          </label>

          <label className="field__checkbox">
            <input
              type="checkbox"
              checked={values.is_authorized_requester}
              onChange={(event) => {
                set('is_authorized_requester', event.target.checked);
              }}
            />
            {t('crm.authorizedRequesterHint')}
          </label>

          {/* Widerspruch nach Art. 21 DSGVO -- docs/04, Abschnitt 9. Er
              unterbindet Umfragen und Benachrichtigungen dauerhaft; die
              Regel setzt der Server durch, nicht diese Maske. */}
          <label className="field__checkbox">
            <input
              type="checkbox"
              checked={values.objects_to_contact}
              onChange={(event) => {
                set('objects_to_contact', event.target.checked);
              }}
            />
            {t('crm.objectsToContactHint')}
          </label>

          {create.isError ? <ErrorNotice error={create.error} /> : null}
          {update.isError ? <ErrorNotice error={update.error} /> : null}

          <div className="row">
            <Button
              variant="primary"
              disabled={!values.last_name.trim()}
              busy={create.isPending || update.isPending}
              onClick={() => {
                const payload = {
                  last_name: values.last_name.trim(),
                  first_name: values.first_name.trim(),
                  is_primary: values.is_primary,
                  is_authorized_requester: values.is_authorized_requester,
                  objects_to_contact: values.objects_to_contact,
                  ...(values.email ? { email: values.email } : {}),
                  ...(values.phone ? { phone: values.phone } : {}),
                  ...(values.position ? { position: values.position } : {}),
                  ...(values.site_id ? { site_id: values.site_id } : {}),
                };

                const done = { onSuccess: reset };
                if (editing !== null) {
                  update.mutate({ contactId: editing, payload }, done);
                  return;
                }
                create.mutate(payload, done);
              }}
            >
              {editing === null ? t('masterdata.add') : t('app.save')}
            </Button>
            {editing !== null ? (
              <Button variant="ghost" onClick={reset}>
                {t('app.cancel')}
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}
